/**
 * Colour handling for Follow Up entry backgrounds.
 *
 * The picker is free hex by request, not a fixed palette, which means a user can
 * choose a background that would hide the label. Rather than restrict the
 * picker, the label's colour is DERIVED from the background here — so any hex
 * the user lands on stays readable, in either theme.
 */

/** Tailwind gray-900 / gray-50 — the app's existing text extremes. */
const DARK_TEXT = '#111827';
const LIGHT_TEXT = '#F9FAFB';

/** Mirrors `_HEX_COLOR` in follow_ups.py, so the client never sends a 422. */
const HEX = /^#[0-9A-Fa-f]{6}$/;

export const isValidHex = (value: string | null | undefined): boolean =>
  !!value && HEX.test(value.trim());

/**
 * Upper-case a valid hex, or null for anything blank/invalid (which clears the
 * colour). The native picker emits lower case and the backend stores upper —
 * matching here keeps an optimistic update from visibly flickering to a
 * different string when the saved row arrives.
 */
export const normalizeHex = (value: string | null | undefined): string | null => {
  const trimmed = (value ?? '').trim();
  if (!isValidHex(trimmed)) return null;
  return trimmed.toUpperCase();
};

/** sRGB relative luminance (WCAG). Green is weighted far above blue because
 *  that is how the eye reads brightness — a naive channel average calls pure
 *  blue "light" and pure green "dark", getting both backwards. */
const luminance = (r: number, g: number, b: number): number => {
  const channel = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
};

/**
 * The label colour to use on `background`. Falls back to dark text on anything
 * unusable and never throws — this runs inline in a render for every entry on
 * the panel, so a stray value must degrade, not blow up the list.
 *
 * A leading `#` is optional so a raw value from a stored row or an input event
 * both work.
 */
export const readableTextOn = (background: string | null | undefined): string => {
  const raw = (background ?? '').trim().replace(/^#/, '');
  if (!/^[0-9A-Fa-f]{6}$/.test(raw)) return DARK_TEXT;

  const r = parseInt(raw.slice(0, 2), 16);
  const g = parseInt(raw.slice(2, 4), 16);
  const b = parseInt(raw.slice(4, 6), 16);

  // 0.5 splits the range at the perceptual midpoint closely enough for a
  // one-line label; the exact boundary colour is a coin toss either way.
  return luminance(r, g, b) > 0.5 ? DARK_TEXT : LIGHT_TEXT;
};
